import { useCallback, useRef, useState } from 'react';

import { css, keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { Skeleton, Typography } from '@kc-console/common-ui';
import IcHeartFill from '@kep/kcds-icons/IcHeartFill';

import useAnnouncementHeart from './useAnnouncementHeart';

import type { CSSProperties } from 'react';

/** 동시에 퍼질 수 있는 물결 최대 개수 */
const MAX_RIPPLES = 12;

/** 한 번만 눌러도 반응이 보이도록 하는 최소 강도 (0 ~ 1) */
const HEAT_BASE = 0.5;

/** 연타 강도가 최대치에 도달하기까지 필요한 대기 하트 수 */
const HEAT_SATURATION_COUNT = 10;

interface AnnouncementHeartButtonProps {
  /** 하트를 보낼 새소식 ID */
  announcementId: string;
}

/** 사용자가 동작 최소화를 선택했는지 확인합니다. */
const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * 같은 애니메이션을 연타로 다시 재생시킵니다.
 * @description
 * - 클래스를 뗐다 붙이는 사이에 reflow 를 강제해야 브라우저가 애니메이션을 처음부터 다시 그립니다.
 */
const restartAnimation = (element: HTMLElement | null, className: string) => {
  if (!element) return;

  element.classList.remove(className);
  void element.offsetWidth;
  element.classList.add(className);
};

/**
 * 새소식 하트 버튼입니다.
 * @description
 * - 클릭은 횟수 제한 없이 계속 받으며, 누를 때마다 하트가 튀어오르고 물결이 퍼집니다.
 * - 물결은 클릭마다 새로 만들어 각자 수명을 다합니다.
 *   하나를 돌려 쓰면 연타할 때 재생 중인 물결이 끊기고 처음부터 다시 그려져 덜컹거립니다.
 * - 서버 전송은 useAnnouncementHeart 가 디바운스로 모아서 처리하므로 연타해도 요청이 폭주하지 않습니다.
 * - 동작 최소화(prefers-reduced-motion) 환경에서는 물결을 만들지 않고 카운트만 올립니다.
 */
export default function AnnouncementHeartButton({ announcementId }: AnnouncementHeartButtonProps) {
  const { addHeart, count, isCountReady, pendingCount } = useAnnouncementHeart(announcementId);
  const [ripples, setRipples] = useState<number[]>([]);
  const rippleIdRef = useRef(0);
  const iconRef = useRef<HTMLSpanElement>(null);

  const handleRemoveRipple = useCallback((id: number) => {
    setRipples(previous => previous.filter(rippleId => rippleId !== id));
  }, []);

  const handleClick = () => {
    addHeart();

    if (prefersReducedMotion()) return;

    restartAnimation(iconRef.current, POP_CLASS);
    rippleIdRef.current += 1;
    setRipples(previous => [...previous, rippleIdRef.current].slice(-MAX_RIPPLES));
  };

  /**
   * 버튼 그라데이션·글로우의 세기입니다.
   * @description
   * - 누른 직후 바로 보이도록 기본값을 주고, 연타할수록 진해집니다.
   * - 모아둔 하트를 서버로 보내고 나면 0 이 되므로, 손을 떼면 자연스럽게 사그라듭니다.
   */
  const heat =
    pendingCount === 0 ? 0 : Math.min(HEAT_BASE + pendingCount / HEAT_SATURATION_COUNT, 1);

  return (
    <HeartButton
      type="button"
      aria-label="이 새소식에 하트 보내기"
      style={{ '--heart-heat': heat } as CSSProperties}
      onClick={handleClick}
    >
      <IconSlot>
        {ripples.map(rippleId => (
          <Ripple
            key={rippleId}
            aria-hidden="true"
            onAnimationEnd={() => handleRemoveRipple(rippleId)}
          />
        ))}
        <HeartIcon ref={iconRef}>
          <IcHeartFill />
        </HeartIcon>
      </IconSlot>
      {/*
        조회 전에도 카운트 자리를 비워두지 않습니다.
        숫자가 없다가 생기면 버튼 너비가 한 번 벌어져 덜컹거립니다.
      */}
      <CountSlot>
        {isCountReady ? (
          <CountValue key={count}>
            <Typography variant="label/medium/xs" color="text/default">
              {count.toLocaleString()}
            </Typography>
          </CountValue>
        ) : (
          <Skeleton shape="rectangle" width={20} height={10} aria-hidden="true" />
        )}
      </CountSlot>
    </HeartButton>
  );
}

/** 하트 아이콘이 튀어오르는 애니메이션 클래스 */
const POP_CLASS = 'is-popping';

const popKeyframes = keyframes`
  0% { transform: scale(1); }
  26% { transform: scale(0.9) rotate(-3deg); }
  58% { transform: scale(1.16) rotate(2deg); }
  100% { transform: scale(1) rotate(0deg); }
`;

const rippleKeyframes = keyframes`
  0% { opacity: 0.34; transform: translate(-50%, -50%) scale(0.3); }
  100% { opacity: 0; transform: translate(-50%, -50%) scale(2.6); }
`;

const rollUpKeyframes = keyframes`
  0% { opacity: 0; transform: translateY(6px); }
  100% { opacity: 1; transform: translateY(0); }
`;

const HeartButton = styled('button')(
  ({ theme }) => css`
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    flex-shrink: 0;
    height: 28px;
    padding: 0 10px;
    margin: 0;
    border: 1px solid ${theme.color['border/default']};
    border-radius: 999px;
    background-color: ${theme.color['surface/default']};
    cursor: pointer;
    transition:
      background-color 160ms ease,
      border-color 160ms ease,
      box-shadow 200ms ease,
      transform 160ms ease;

    /* 누른 직후엔 진하고, 보내고 나면(--heart-heat: 0) 서서히 사그라듭니다. */
    box-shadow: 0 0 0 calc(var(--heart-heat, 0) * 4px)
      ${theme.color['decorativeSurface/weaker/red']};

    /* 그라데이션은 배경색과 달리 전환이 보간되지 않아, 덧씌운 층의 투명도로 나타냅니다. */
    &::before {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: inherit;
      background: linear-gradient(
        120deg,
        ${theme.color['decorativeSurface/weakest/red']} 0%,
        ${theme.color['decorativeSurface/weaker/red']} 52%,
        ${theme.color['decorativeSurface/weaker/orange']} 100%
      );
      opacity: var(--heart-heat, 0);
      transition: opacity 280ms ease;
      pointer-events: none;
    }

    &:hover {
      border-color: ${theme.color['decorativeBorder/red']};
    }

    &:active {
      transform: scale(0.96);
    }

    &:focus-visible {
      outline: 2px solid ${theme.color['border/select']};
      outline-offset: 2px;
    }
  `,
);

const IconSlot = styled('span')`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

/*
  숫자를 고정폭(tabular-nums)으로 그려, 자릿수가 같으면 카운트가 바뀌어도 버튼 폭이 흔들리지 않게 합니다.
  최소 두 자리 폭을 잡아 두어 99 까지는 버튼 폭이 그대로이고, 그 뒤로는 자릿수가 바뀔 때만 늘어납니다.
  ch 단위가 카운트 글자 기준이 되도록 같은 타이포그래피를 적용합니다.
*/
const CountSlot = styled('span')(
  ({ theme }) => css`
    ${theme.typography['label/medium/xs']};
    position: relative;
    display: inline-flex;
    overflow: hidden;
    min-width: 2ch;
    font-variant-numeric: tabular-nums;
    height: 18px;
    align-items: center;
    justify-content: center;
  `,
);

const HeartIcon = styled('span')(
  ({ theme }) => css`
    display: inline-flex;
    font-size: 13px;
    color: ${theme.color['decorativeIcon/weak/red']};
    will-change: transform;

    @media (prefers-reduced-motion: no-preference) {
      &.${POP_CLASS} {
        animation: ${popKeyframes} 320ms cubic-bezier(0.22, 0.86, 0.3, 1.1);
      }
    }
  `,
);

const CountValue = styled('span')`
  display: inline-flex;

  @media (prefers-reduced-motion: no-preference) {
    animation: ${rollUpKeyframes} 200ms cubic-bezier(0.22, 0.86, 0.3, 1);
  }
`;

const Ripple = styled('span')(
  ({ theme }) => css`
    position: absolute;
    top: 50%;
    left: 50%;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background-color: ${theme.color['decorativeSurface/red']};
    opacity: 0;
    pointer-events: none;
    transform: translate(-50%, -50%) scale(0.3);
    animation: ${rippleKeyframes} 1000ms cubic-bezier(0.16, 0.7, 0.24, 1) forwards;
  `,
);
